import { TranslationKey } from '../translation/TranslationKey';
import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from '../common/entity';
import { TableSettingsColumn } from '../tableSettings';
import { t } from '../translation/Translator';

export enum ProductDetailColumn {
  stockLocation = 'stockLocation',
  bin = 'bin',
  container = 'container',

  lotNumber = 'lotNumber',
  lotExpiry = 'lotExpiry',
  serial = 'serial',
  lpn = 'lpn',
  kit = 'kit',

  productNumber = 'productNumber',
  productName = 'productName',

  quantity = 'quantity',
}

export class ProductDetailTableSettings extends CompanyEntity {
  columns: ProductDetailTableColumn[] = defaultProductDetailTableColumn;
  userId?: string;

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, cloneDeep(obj));
  }

  forUpdate(): UpdateProductDetailTableSettingsInput {
    return UpdateProductDetailTableSettingsInput.from(this, UpdateProductDetailTableSettingsInput);
  }

  forDelete(): DeleteProductDetailTableSettingsInput {
    return DeleteProductDetailTableSettingsInput.from(this, DeleteProductDetailTableSettingsInput);
  }

  validate() {
    return this.validateEntity(
      Object.getOwnPropertyNames(this) as (keyof ProductDetailTableSettings)[],
      (field: keyof ProductDetailTableSettings) => {
        return null;
      },
    );
  }
}

export class CreateProductDetailTableSettingsInput extends forCreate(ProductDetailTableSettings) {}

export class UpdateProductDetailTableSettingsInput extends forUpdate(ProductDetailTableSettings) {}

export class DeleteProductDetailTableSettingsInput extends forDelete(ProductDetailTableSettings) {}

export class ProductDetailTableColumn implements TableSettingsColumn<ProductDetailColumn> {
  index: number;
  column: string;
  label: string;

  constructor(index: number, column: string, label: string) {
    this.index = index;
    this.column = column;
    this.label = label;
  }
}

export const defaultProductDetailTableColumn: ProductDetailTableColumn[] = [
  {
    index: 0,
    column: ProductDetailColumn.stockLocation,
    label: t(TranslationKey.stockLocation).singular.label,
  },
  {
    index: 1,
    column: ProductDetailColumn.bin,
    label: t(TranslationKey.bin).singular.label,
  },
  {
    index: 2,
    column: ProductDetailColumn.container,
    label: t(TranslationKey.container).singular.label,
  },
  {
    index: 3,
    column: ProductDetailColumn.serial,
    label: t(TranslationKey.serial).singular.label,
  },
  {
    index: 4,
    column: ProductDetailColumn.lpn,
    label: t(TranslationKey.lpn).singular.label.toUpperCase(),
  },
  {
    index: 5,
    column: ProductDetailColumn.kit,
    label: t(TranslationKey.kit).singular.label,
  },
  {
    index: 6,
    column: ProductDetailColumn.lotNumber,
    label: t(TranslationKey.lot).singular.label,
  },
  {
    index: 7,
    column: ProductDetailColumn.lotExpiry,
    label: t(TranslationKey.expirationDate).singular.label,
  },
  {
    index: 100,
    column: ProductDetailColumn.quantity,
    label: t(TranslationKey.status).singular.label,
  },
];

export function productColumnToString(col: string) {
  switch (col) {
    case ProductDetailColumn.stockLocation:
      return t(TranslationKey.stockLocation).singular.label;
    case ProductDetailColumn.bin:
      return t(TranslationKey.bin).singular.label;
    case ProductDetailColumn.container:
      return t(TranslationKey.container).singular.label;
    case ProductDetailColumn.serial:
      return t(TranslationKey.serial).singular.label;
    case ProductDetailColumn.lpn:
      return t(TranslationKey.lpn).singular.label.toUpperCase();
    case ProductDetailColumn.lotNumber:
      return t(TranslationKey.lot).singular.label;
    case ProductDetailColumn.lotExpiry:
      return t(TranslationKey.expirationDate).singular.label;
    case ProductDetailColumn.quantity:
      return t(TranslationKey.status).singular.label;
  }
  return col;
}

export function stringToProductColumn(col: string) {
  switch (col) {
    case t(TranslationKey.stockLocation).singular.label:
      return ProductDetailColumn.stockLocation;
    case t(TranslationKey.bin).singular.label:
      return ProductDetailColumn.bin;
    case t(TranslationKey.container).singular.label:
      return ProductDetailColumn.container;
    case t(TranslationKey.serial).singular.label:
      return ProductDetailColumn.serial;
    case t(TranslationKey.lpn).singular.label.toUpperCase():
      return ProductDetailColumn.lpn;
    case t(TranslationKey.lot).singular.label:
      return ProductDetailColumn.lotNumber;
    case t(TranslationKey.expirationDate).singular.label:
      return ProductDetailColumn.lotExpiry;
    case t(TranslationKey.status).singular.label:
      return ProductDetailColumn.quantity;
  }
  return col;
}
